'use client';

import React, { Suspense, useState, useEffect, useRef, FormEvent } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { getSafeErrorMessage } from '@/lib/error-codes';
import { authApi } from '@/lib/auth-api';
import ThreeDotLoader from '@/components/common/ThreeDotLoader';
import { Eye, EyeOff } from 'lucide-react';

type LoginMode = 'password' | 'code';

function LoginForm() {
  const { login, isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get('redirect') || '/ecom-image';

  // Already authenticated — send back immediately
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.replace(redirect);
    }
  }, [isLoading, isAuthenticated, redirect, router]);

  const [mode, setMode] = useState<LoginMode>('password');
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [codeSending, setCodeSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const codeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (codeTimerRef.current) clearInterval(codeTimerRef.current);
    };
  }, []);

  const startCountdown = () => {
    setCountdown(60);
    codeTimerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (codeTimerRef.current) clearInterval(codeTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    const phone = account.trim();
    if (!/^1\d{10}$/.test(phone)) {
      setError('请先输入正确的手机号');
      return;
    }
    setError('');
    setCodeSending(true);
    try {
      await authApi.sendLoginCode(phone);
      startCountdown();
    } catch (err: unknown) {
      setError(getSafeErrorMessage(err, '验证码发送失败'));
    } finally {
      setCodeSending(false);
    }
  };

  const validate = (): boolean => {
    if (!account.trim()) { setError('请输入手机号或邮箱'); return false; }
    if (mode === 'code') {
      if (!/^1\d{10}$/.test(account.trim())) { setError('验证码登录请输入手机号'); return false; }
      if (!verifyCode.trim()) { setError('请输入验证码'); return false; }
      return true;
    }
    if (!password) { setError('请输入密码'); return false; }
    if (password.length < 6) { setError('密码至少6位'); return false; }
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await login(account.trim(), password, mode === 'code' ? { verifyCode: verifyCode.trim() } : undefined);
      router.replace(redirect);
    } catch (err: unknown) {
      setError(getSafeErrorMessage(err, '登录失败，请检查账号和密码'));
    } finally {
      setLoading(false);
    }
  };

  // 会话恢复期间不渲染表单：已登录用户经过 /login 时等鉴权完成直接跳转，
  // 避免登录框（连同浏览器自动填充的密码）闪现后被自动带离。
  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-70px)] flex items-center justify-center bg-bg-secondary">
        <ThreeDotLoader />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-70px)] flex items-center justify-center px-4 bg-bg-secondary">
      {/* Decorative gradient blobs */}
      <div className="fixed top-20 left-1/4 w-[400px] h-[400px] bg-primary-purple/20 rounded-full blur-[120px] pointer-events-none" />
      <div className="fixed bottom-20 right-1/4 w-75 h-75 bg-primary-pink/15 rounded-full blur-[100px] pointer-events-none" />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-[420px] rounded-xl p-8 glass-panel animate-scale-in"
      >
        <h1 className="text-2xl font-medium text-center mb-2">欢迎回来</h1>
        <p className="text-sm text-white/60 text-center mb-6">登录 FlowX Pro 开始使用</p>

        {/* 登录方式切换 */}
        <div className="flex mb-6 rounded-[10px] bg-white/5 border border-white/10 p-1">
          <button
            type="button"
            onClick={() => { setMode('password'); setError(''); }}
            className={`flex-1 h-8 rounded-[7px] text-sm transition-colors ${mode === 'password' ? 'bg-primary-purple text-white' : 'text-white/60 hover:text-white'}`}
          >
            密码登录
          </button>
          <button
            type="button"
            onClick={() => { setMode('code'); setError(''); }}
            className={`flex-1 h-8 rounded-[7px] text-sm transition-colors ${mode === 'code' ? 'bg-primary-purple text-white' : 'text-white/60 hover:text-white'}`}
          >
            验证码登录
          </button>
        </div>

        {error && (
          <div className="mb-4 px-4 py-2.5 rounded-md bg-error/10 border border-error/30 text-error text-sm">
            {error}
          </div>
        )}

        {/* Account */}
        <label className="block mb-1.5 text-sm text-white/60">
          {mode === 'code' ? '手机号' : '手机号 / 邮箱'}
        </label>
        <input
          type="text"
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          placeholder={mode === 'code' ? '请输入手机号' : '请输入手机号或邮箱'}
          maxLength={mode === 'code' ? 11 : undefined}
          className="w-full h-11 px-4 rounded-[10px] bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 outline-hidden focus:border-primary-purple transition-colors mb-5"
        />

        {mode === 'password' && (
          <>
            {/* Password */}
            <label className="block mb-1.5 text-sm text-white/60">密码</label>
            <div className="relative mb-2">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入密码"
                className="w-full h-11 px-4 pr-11 rounded-[10px] bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 outline-hidden focus:border-primary-purple transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                aria-label={showPwd ? '隐藏密码' : '显示密码'}
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </>
        )}

        {mode === 'code' && (
          <>
            {/* Verify code */}
            <label className="block mb-1.5 text-sm text-white/60">验证码</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                placeholder="请输入验证码"
                maxLength={6}
                className="flex-1 h-11 px-4 rounded-[10px] bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 outline-hidden focus:border-primary-purple transition-colors"
              />
              <button
                type="button"
                onClick={handleSendCode}
                disabled={codeSending || countdown > 0}
                className="flex-shrink-0 h-11 px-3 rounded-[10px] border border-white/10 bg-white/5 text-sm text-white/80 hover:text-white hover:border-primary-purple transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {codeSending ? '发送中...' : countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}
              </button>
            </div>
          </>
        )}

        {/* Remember */}
        <div className="flex items-center mb-6">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-white/30 bg-transparent accent-primary-purple"
            />
            <span className="text-sm text-white/60">记住我</span>
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-11 rounded-md bg-linear-to-r from-[#652DF5] to-[#D24DDA] text-white text-sm font-medium hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '登录中...' : '登录'}
        </button>
      </form>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginForm />
    </Suspense>
  );
}
